import { Fragment, useEffect, useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import {
  ViewportCoordinates,
  FloorplanCoordinates,
  computeBoundingRegionExtents,
  snapToAngle,
  computePolygonEdges,
} from 'lib/geometry';
import { lineSegmentIntersection2d } from 'lib/algorithm';
import FloorplanCollection from 'lib/floorplan-collection';
import PlanSensor, { OpenAreaFunction, EntryFunction } from 'lib/sensor';
import Space from 'lib/space';
import Reference from 'lib/reference';
import Threshold from 'lib/threshold';
import PhotoGroup from 'lib/photo-group';
import WallSegment, {
  WALL_SEGMENT_SNAP_THRESHOLD_PIXELS,
} from 'lib/wall-segment';

import { PlacementMode } from 'components/floorplan/placement-mode';
import {
  Layer,
  MetricLabel,
  useFloorplanLayerContext,
  drawPolygon,
  toRawHex,
} from 'components/floorplan';
import AreaOfConcern from 'lib/area-of-concern';

import {
  Gray700,
  Purple400,
  Yellow400,
} from '@density/dust/dist/tokens/dust.tokens';
import PolygonalSpaceCreationLayer from './polygonal-space-creation-layer';
import AreaOfConcernCreationLayer from './area-of-concern-creation-layer';
import ReferenceRulerCreationLayer from './reference-ruler-creation-layer';
import ThresholdCreationLayer from './threshold-creation-layer';

const PLACEMENT_TOOLTIP_OFFSET_X_PX = 16;
const DESK_BENCH_PREVIEW_SHAPE_NAME = 'desk-bench-creation-shape';
const DESK_SPACE_NAME_PREFIX = 'Desk';
const DESK_SPACE_FUNCTION = 'desk' as Space['coreSpaceFunction'];

type DeskBenchSideMode = 'both' | 'left' | 'right';

function getDeskBenchSides(sideMode: DeskBenchSideMode): Array<number> {
  switch (sideMode) {
    case 'left':
      return [-1];
    case 'right':
      return [1];
    case 'both':
      return [1, -1];
  }
}

function cycleDeskBenchSideMode(
  sideMode: DeskBenchSideMode
): DeskBenchSideMode {
  switch (sideMode) {
    case 'both':
      return 'right';
    case 'right':
      return 'left';
    case 'left':
      return 'both';
  }
}

function isTypingTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function computeDeskBenchPolygons(
  positionA: FloorplanCoordinates,
  positionB: FloorplanCoordinates,
  deskWidthMeters: number,
  deskDepthMeters: number,
  sideMode: DeskBenchSideMode
): Array<Array<FloorplanCoordinates>> {
  const dx = positionB.x - positionA.x;
  const dy = positionB.y - positionA.y;
  const lengthMeters = Math.hypot(dx, dy);

  if (lengthMeters <= 0 || deskWidthMeters <= 0 || deskDepthMeters <= 0) {
    return [];
  }

  const desksPerSide = Math.floor(lengthMeters / deskWidthMeters);
  if (desksPerSide < 1) {
    return [];
  }

  const ux = dx / lengthMeters;
  const uy = dy / lengthMeters;
  const px = -uy;
  const py = ux;
  const sides = getDeskBenchSides(sideMode);

  const makePoint = (alongMeters: number, offsetMeters: number) =>
    FloorplanCoordinates.create(
      positionA.x + ux * alongMeters + px * offsetMeters,
      positionA.y + uy * alongMeters + py * offsetMeters
    );

  const polygons: Array<Array<FloorplanCoordinates>> = [];
  for (let i = 0; i < desksPerSide; i += 1) {
    const segmentStart = i * deskWidthMeters;
    const segmentEnd = segmentStart + deskWidthMeters;

    sides.forEach((side) => {
      polygons.push([
        makePoint(segmentStart, 0),
        makePoint(segmentEnd, 0),
        makePoint(segmentEnd, side * deskDepthMeters),
        makePoint(segmentStart, side * deskDepthMeters),
      ]);
    });
  }

  return polygons;
}

function createDeskBenchSpaces(
  positionA: FloorplanCoordinates,
  positionB: FloorplanCoordinates,
  existingSpaces: Array<Space>,
  countingMode: 'oa' | 'waffle',
  deskWidthMeters: number,
  deskDepthMeters: number,
  sideMode: DeskBenchSideMode,
  initialDeskNumber: number
): { spaces: Array<Space>; nextDeskNumber: number } {
  const spaces: Array<Space> = [];
  const polygons = computeDeskBenchPolygons(
    positionA,
    positionB,
    deskWidthMeters,
    deskDepthMeters,
    sideMode
  );
  let nextDeskNumber = Math.max(
    initialDeskNumber,
    Space.getNextGeneratedNameNumber(existingSpaces, DESK_SPACE_NAME_PREFIX)
  );

  polygons.forEach((vertices) => {
    const spaceName = Space.generateNameWithPrefix(
      [...existingSpaces, ...spaces],
      DESK_SPACE_NAME_PREFIX,
      nextDeskNumber
    );
    const deskNumber = Space.getGeneratedNameNumber(
      spaceName,
      DESK_SPACE_NAME_PREFIX
    );
    nextDeskNumber = (deskNumber ?? nextDeskNumber) + 1;

    const space = Space.createPolygon(vertices, spaceName, countingMode);
    space.coreSpaceCapacity = 1;
    space.coreSpaceFunction = DESK_SPACE_FUNCTION;
    spaces.push(space);
  });

  return { spaces, nextDeskNumber };
}

// The object placement layer renders information about objects about to be placed, and facilutates
// rendering a backdrop over top of all other elements so clicks anywhere on the floorplan are
// trapped.
const ObjectPlacementTargetLayer: React.FunctionComponent<{
  placementMode: PlacementMode | null;
  openAreaSensorType: OpenAreaFunction;
  entrySensorType: EntryFunction;
  walls: FloorplanCollection<WallSegment>;
  sensors: FloorplanCollection<PlanSensor>;
  areasOfConcern: FloorplanCollection<AreaOfConcern>;
  onAddSensor: (sensor: PlanSensor) => void;
  onAddAreaOfConcern: (areaOfConcern: AreaOfConcern) => void;
  spaces: FloorplanCollection<Space>;
  onAddSpace: (space: Space) => void;
  onAddSpaces: (
    spaces: Array<Space>,
    nextPlacementMode?: PlacementMode | null
  ) => void;
  onAddReference: (reference: Reference) => void;
  photoGroups: FloorplanCollection<PhotoGroup>;
  onAddPhotoGroup: (photoGroup: PhotoGroup) => void;
  onAddThreshold: (threshold: Threshold) => void;
  focusedObject?: null | {
    type: 'sensor' | 'areaofconcern' | 'space' | 'layer' | 'threshold';
    id: string;
  };
}> = ({
  placementMode,
  openAreaSensorType,
  entrySensorType,
  walls,
  sensors,
  areasOfConcern,
  onAddSensor,
  onAddAreaOfConcern,
  spaces,
  onAddSpace,
  onAddSpaces,
  onAddReference,
  photoGroups,
  onAddPhotoGroup,
  onAddThreshold,
  focusedObject = null,
}) => {
  const context = useFloorplanLayerContext();

  const mousePositionRef = useRef<ViewportCoordinates | null>(null);

  // Cache a copy of "placementMode" locally so that state updates can be made quicker and not have
  // to go through the whole flux loop within the editor
  const placementModeRef = useRef<PlacementMode | null>(null);

  // Globally add a way that cypress can interrogate the object placement layer placement mode ref
  // This is used by the planning tab "page object" in the cypress tests
  if (
    process.env.REACT_APP_ENABLE_EDITOR_GET_STATE &&
    process.env.REACT_APP_ENABLE_EDITOR_GET_STATE.toLowerCase() === 'true'
  ) {
    (window as any).editorGetPlacementModeRef = () => placementModeRef;
  }

  const onMouseDownCanvasRef = useRef<((evt: MouseEvent) => void) | null>(null);
  const onMouseMoveCanvasRef = useRef<((evt: MouseEvent) => void) | null>(null);
  const onMouseOutCanvasRef = useRef<((evt: MouseEvent) => void) | null>(null);
  const onKeyDownWindowRef = useRef<((evt: KeyboardEvent) => void) | null>(
    null
  );

  const onMouseDown = useCallback(
    (floorplanCoords: FloorplanCoordinates, evt: MouseEvent) => {
      if (!placementModeRef.current) {
        return;
      }

      switch (placementModeRef.current.type) {
        case 'sensor': {
          const sensorType = placementModeRef.current.sensorType;
          const filteredSensors = PlanSensor.filterByType(sensorType, sensors);
          const sensorName = PlanSensor.generateName(
            sensorType,
            filteredSensors.length
          );

          const sensorFunction: EntryFunction | OpenAreaFunction = 'oa'
            ? openAreaSensorType
            : entrySensorType;

          const planSensor = PlanSensor.create(
            sensorType,
            sensorType === 'oa' ? openAreaSensorType : entrySensorType,
            floorplanCoords,
            PlanSensor.computeDefaultHeight(sensorFunction),
            0,
            sensorName
          );
          onAddSensor(planSensor);
          return;
        }
        case 'areaofconcern-duplicate': {
          // A default polygon - this will get overridden in the placement.addSpace action with
          // data in state.duplicateSpacePolygonParams
          const vertices = [
            FloorplanCoordinates.create(
              floorplanCoords.x,
              floorplanCoords.y - 2
            ),
            FloorplanCoordinates.create(
              floorplanCoords.x + 2,
              floorplanCoords.y + 2
            ),
            FloorplanCoordinates.create(
              floorplanCoords.x - 2,
              floorplanCoords.y + 2
            ),
          ];

          const areaOfConcern = AreaOfConcern.create(vertices, 'AOC');
          onAddAreaOfConcern(areaOfConcern);
          return;
        }
        case 'areaofconcern': {
          // If the next point cannot be placed due to a self intersection, then bail
          if (placementModeRef.current.nextPointSelfIntersection) {
            return;
          }

          // If the user clicks on the final point, then finish the polygon!
          if (placementModeRef.current.mouseOverFinalPoint) {
            const areaName = AreaOfConcern.generateName(
              FloorplanCollection.list(areasOfConcern)
            );
            const areaOfConcern = AreaOfConcern.create(
              placementModeRef.current.vertices,
              areaName
            );
            onAddAreaOfConcern(areaOfConcern);
            return;
          }

          // If not close to the first vertex, add a new vertex
          if (!placementModeRef.current.nextPointPosition) {
            return;
          }
          placementModeRef.current = {
            ...placementModeRef.current,
            vertices: [
              ...placementModeRef.current.vertices,
              placementModeRef.current.nextPointPosition,
            ],
          };
          return;
        }
        case 'space': {
          const spaceName = Space.generateName(
            FloorplanCollection.list(spaces)
          );
          switch (placementModeRef.current.shape) {
            case 'desk-bench': {
              if (!placementModeRef.current.positionA) {
                placementModeRef.current = {
                  ...placementModeRef.current,
                  positionA: floorplanCoords,
                };
                return;
              }

              let positionB =
                placementModeRef.current.positionB || floorplanCoords;
              if (evt.shiftKey) {
                positionB = snapToAngle(
                  placementModeRef.current.positionA,
                  positionB
                );
              }

              const deskBenchResult = createDeskBenchSpaces(
                placementModeRef.current.positionA,
                positionB,
                FloorplanCollection.list(spaces),
                placementModeRef.current.countingMode,
                placementModeRef.current.deskWidthMeters,
                placementModeRef.current.deskDepthMeters,
                placementModeRef.current.sideMode,
                placementModeRef.current.nextDeskNumber
              );
              if (deskBenchResult.spaces.length > 0) {
                const nextPlacementMode = {
                  ...placementModeRef.current,
                  nextDeskNumber: deskBenchResult.nextDeskNumber,
                  positionA: null,
                  positionB: null,
                  mousePosition: null,
                };
                placementModeRef.current = nextPlacementMode;
                onAddSpaces(deskBenchResult.spaces, nextPlacementMode);
              }
              return;
            }
            case 'box': {
              const space = Space.createBox(
                floorplanCoords,
                spaceName,
                Space.DEFAULT_BOX_SHAPE.width,
                Space.DEFAULT_BOX_SHAPE.height,
                placementModeRef.current.countingMode
              );
              onAddSpace(space);
              return;
            }
            case 'circle': {
              const space = Space.createCircle(
                floorplanCoords,
                spaceName,
                Space.DEFAULT_CIRCLE_SHAPE.radius
              );
              onAddSpace(space);
              return;
            }
            case 'polygon': {
              // If the next point cannot be placed due to a self intersection, then bail
              if (placementModeRef.current.nextPointSelfIntersection) {
                return;
              }

              // If the user clicks on the final point, then finish the polygon!
              if (placementModeRef.current.mouseOverFinalPoint) {
                const space = Space.createPolygon(
                  placementModeRef.current.vertices,
                  spaceName,
                  placementModeRef.current.countingMode
                );
                onAddSpace(space);
                return;
              }

              // If not close to the first vertex, add a new vertex
              if (!placementModeRef.current.nextPointPosition) {
                return;
              }
              placementModeRef.current = {
                ...placementModeRef.current,
                vertices: [
                  ...placementModeRef.current.vertices,
                  placementModeRef.current.nextPointPosition,
                ],
              };
              return;
            }
            case 'polygon-duplicate': {
              // A default polygon - this will get overridden in the placement.addSpace action with
              // data in state.duplicateSpacePolygonParams
              const vertices = [
                FloorplanCoordinates.create(
                  floorplanCoords.x,
                  floorplanCoords.y - 2
                ),
                FloorplanCoordinates.create(
                  floorplanCoords.x + 2,
                  floorplanCoords.y + 2
                ),
                FloorplanCoordinates.create(
                  floorplanCoords.x - 2,
                  floorplanCoords.y + 2
                ),
              ];

              const space = Space.createPolygon(
                vertices,
                spaceName,
                placementModeRef.current.countingMode
              );
              onAddSpace(space);
              return;
            }
            default: {
              return;
            }
          }
        }
        case 'reference': {
          switch (placementModeRef.current.referenceType) {
            case 'ruler': {
              // If starting position is not set, set it on the first click
              if (!placementModeRef.current.positionA) {
                placementModeRef.current = {
                  ...placementModeRef.current,
                  positionA: floorplanCoords,
                };
                return;
              }
              if (!placementModeRef.current.positionB) {
                // Make sure that the mouse has been moved so that position B is assigned
                return;
              }

              const referenceRuler = Reference.createRuler(
                placementModeRef.current.positionA,
                placementModeRef.current.positionB
              );
              onAddReference(referenceRuler);
              return;
            }
            case 'height': {
              if (!placementModeRef.current.mousePosition) {
                return;
              }
              const referenceHeight = Reference.createHeight(
                placementModeRef.current.mousePosition
              );
              onAddReference(referenceHeight);
              return;
            }
            default: {
              return;
            }
          }
        }
        case 'threshold': {
          // If starting position is not set, set it on the first click
          if (!placementModeRef.current.positionA) {
            placementModeRef.current = {
              ...placementModeRef.current,
              positionA: floorplanCoords,
            };
            return;
          }
          if (!placementModeRef.current.positionB) {
            // Make sure that the mouse has been moved so that position B is assigned
            return;
          }

          const { positionA, positionB } = placementModeRef.current;
          const focusedSensor =
            focusedObject?.type === 'sensor'
              ? FloorplanCollection.list(sensors).find(
                  (sensor) => sensor.id === focusedObject.id
                )
              : undefined;

          const threshold = Threshold.createThreshold(
            '',
            positionA,
            positionB,
            false,
            focusedSensor ? [focusedSensor.id] : []
          );

          onAddThreshold(threshold);
          return;
        }
        case 'photogroup': {
          const photoGroupName = PhotoGroup.generateName(photoGroups);
          const photoGroup = PhotoGroup.create(photoGroupName, floorplanCoords);
          onAddPhotoGroup(photoGroup);
          return;
        }
      }
    },
    [
      sensors,
      onAddSensor,
      spaces,
      onAddSpace,
      onAddSpaces,
      areasOfConcern,
      onAddAreaOfConcern,
      onAddReference,
      photoGroups,
      onAddPhotoGroup,
      openAreaSensorType,
      entrySensorType,
      onAddThreshold,
      focusedObject,
    ]
  );

  const onMouseMove = useCallback(
    (floorplanCoords: FloorplanCoordinates, evt: MouseEvent) => {
      if (!context.viewport.current) {
        return;
      }
      const viewport = context.viewport.current;
      if (!placementModeRef.current) {
        return;
      }

      // Note down the cursor position
      placementModeRef.current = {
        ...placementModeRef.current,
        mousePosition: floorplanCoords,
      };

      // A few special behaviors when placing polygonal spaces:
      if (
        floorplanCoords &&
        placementModeRef.current.type === 'space' &&
        placementModeRef.current.shape === 'polygon'
      ) {
        let position = floorplanCoords;
        let positionViewport = FloorplanCoordinates.toViewportCoordinates(
          position,
          context.floorplan,
          viewport
        );

        placementModeRef.current = {
          type: 'space' as const,
          shape: 'polygon' as const,
          countingMode: placementModeRef.current.countingMode,
          vertices: placementModeRef.current.vertices,
          mousePosition: floorplanCoords,
          nextPointPosition: position,
          mouseOverFinalPoint: false,
          nextPointSelfIntersection:
            placementModeRef.current.nextPointSelfIntersection,
        };

        // Apply any angle snapping, if enabled
        const angleSnappingEnabled = evt.shiftKey;
        if (
          angleSnappingEnabled &&
          placementModeRef.current.vertices.length > 0
        ) {
          const finalVertexViewport =
            FloorplanCoordinates.toViewportCoordinates(
              placementModeRef.current.vertices[
                placementModeRef.current.vertices.length - 1
              ],
              context.floorplan,
              viewport
            );
          const snappedCoordinates = snapToAngle(
            finalVertexViewport,
            positionViewport
          );

          // Update the position to be at the snap result point
          positionViewport = ViewportCoordinates.create(
            snappedCoordinates.x,
            snappedCoordinates.y
          );
          position = ViewportCoordinates.toFloorplanCoordinates(
            positionViewport,
            viewport,
            context.floorplan
          );
        }

        // Snap to walls if they exist
        if (
          !FloorplanCollection.isEmpty(walls) ||
          !FloorplanCollection.isEmpty(spaces)
        ) {
          // Get all wall segments that should be checked for snapping
          const verticesPlusPosition = [
            ...placementModeRef.current.vertices,
            floorplanCoords, // The actual mouse position
            position, // Potentially the snap point position which could be different than the mouse position
          ];
          let [upperLeft, lowerRight] =
            computeBoundingRegionExtents(verticesPlusPosition);
          if (upperLeft && lowerRight) {
            // Pad the bounding box a little on each side so that wall segments within the snap
            // threshold outside of the regular bounding region will be included
            const paddingInPixels =
              WALL_SEGMENT_SNAP_THRESHOLD_PIXELS *
              context.floorplan.scale *
              viewport.zoom;
            upperLeft = FloorplanCoordinates.create(
              upperLeft.x - paddingInPixels,
              upperLeft.y - paddingInPixels
            );
            lowerRight = FloorplanCoordinates.create(
              lowerRight.x + paddingInPixels,
              lowerRight.y + paddingInPixels
            );

            const edgesOfSpaces = FloorplanCollection.list(spaces).flatMap(
              (space) => Space.computeEdgesOfSpace(space)
            );

            const wallSegmentsInRegion =
              WallSegment.computeWallSegmentsInBoundingRegion(
                [
                  ...FloorplanCollection.list(walls),
                  // FIXME: converting each space into "wallsegments" like this seems like a bit of
                  // a bad idea. Come up with a better way of doing this? Like create a more
                  // abstract notion of a "Segment" where a "WalLSegment" is a more specific
                  // version?
                  ...edgesOfSpaces.map(([a, b]) => WallSegment.create(a, b)),
                ],
                upperLeft,
                lowerRight
              );

            const [snapPoint] =
              WallSegment.computeNearestSnapPointInBoundingRegion(
                wallSegmentsInRegion,
                position,
                upperLeft,
                lowerRight,
                context.floorplan,
                viewport,
                WALL_SEGMENT_SNAP_THRESHOLD_PIXELS
              );
            if (snapPoint) {
              position = snapPoint;
              positionViewport = FloorplanCoordinates.toViewportCoordinates(
                snapPoint,
                context.floorplan,
                viewport
              );
            }
          }
        }

        // If the mouse if over the final point, then set a flag
        if (placementModeRef.current.vertices.length > 2) {
          const firstVertex = placementModeRef.current.vertices[0];
          const firstVertexViewport =
            FloorplanCoordinates.toViewportCoordinates(
              firstVertex,
              context.floorplan,
              viewport
            );

          const distanceToFirstVertex = Math.hypot(
            Math.abs(positionViewport.y - firstVertexViewport.y),
            Math.abs(positionViewport.x - firstVertexViewport.x)
          );

          if (distanceToFirstVertex < 8) {
            placementModeRef.current.mouseOverFinalPoint = true;
          }
        }

        // If the mouse intersects a previous line in the polygon, then set a flag
        if (
          placementModeRef.current.vertices.length > 2 &&
          placementModeRef.current.nextPointPosition
        ) {
          const lastVertex =
            placementModeRef.current.vertices[
              placementModeRef.current.vertices.length - 1
            ];

          const testLinePointA = FloorplanCoordinates.toViewportCoordinates(
            lastVertex,
            context.floorplan,
            viewport
          );
          const testLinePointB = FloorplanCoordinates.toViewportCoordinates(
            placementModeRef.current.nextPointPosition,
            context.floorplan,
            viewport
          );

          // Get all edges that make up the polygon being drawn
          const edges = computePolygonEdges(placementModeRef.current.vertices)
            .slice(0, -1)
            .map(([pointA, pointB]) => [
              FloorplanCoordinates.toViewportCoordinates(
                pointA,
                context.floorplan,
                viewport
              ),
              FloorplanCoordinates.toViewportCoordinates(
                pointB,
                context.floorplan,
                viewport
              ),
            ]);

          // Check to see if the new line being drawn intersects with any existing polygon edge
          const intersectingPoints = edges.flatMap((edge, index) => {
            const point = lineSegmentIntersection2d(
              [],
              // The edge is one line segment
              [edge[0].x, edge[0].y],
              [edge[1].x, edge[1].y],
              // And the new edge currently being created is the other line segment
              [testLinePointA.x, testLinePointA.y],
              [testLinePointB.x, testLinePointB.y]
            );

            if (!point) {
              return [];
            }

            // An intersection may be returned for the point where the two edges join, but this is a
            // false positive
            if (
              (point[0] === testLinePointA.x &&
                point[1] === testLinePointA.y) ||
              (point[0] === testLinePointB.x && point[1] === testLinePointB.y)
            ) {
              return [];
            }
            if (
              (edges[edges.length - 1][1].x === point[0] &&
                edges[edges.length - 1][1].y === point[1]) ||
              (edges[edges.length - 1][1].x === point[0] &&
                edges[edges.length - 1][1].y === point[1])
            ) {
              return [];
            }

            return [ViewportCoordinates.create(point[0], point[1])];
          });

          placementModeRef.current.nextPointSelfIntersection =
            intersectingPoints.length > 0
              ? ViewportCoordinates.toFloorplanCoordinates(
                  intersectingPoints[0],
                  viewport,
                  context.floorplan
                )
              : null;
        }

        placementModeRef.current.nextPointPosition = position;
        return;
      }

      if (
        floorplanCoords &&
        placementModeRef.current.type === 'space' &&
        placementModeRef.current.shape === 'desk-bench' &&
        placementModeRef.current.positionA
      ) {
        const positionA = placementModeRef.current.positionA;
        let positionB = floorplanCoords;

        if (evt.shiftKey) {
          positionB = snapToAngle(positionA, positionB);
        }

        placementModeRef.current = {
          ...placementModeRef.current,
          positionA,
          positionB,
        };
        return;
      }

      // When placing reference rulers/thresholds, set the end point equal to the mouse position
      if (
        floorplanCoords &&
        ((placementModeRef.current.type === 'reference' &&
          placementModeRef.current.referenceType === 'ruler' &&
          placementModeRef.current.positionA) ||
          (placementModeRef.current.type === 'threshold' &&
            placementModeRef.current.positionA))
      ) {
        const positionA = placementModeRef.current.positionA;
        let positionB = floorplanCoords;

        // Snap point b if shift is held
        if (evt.shiftKey) {
          positionB = snapToAngle(positionA, positionB);
        }

        placementModeRef.current = {
          ...placementModeRef.current,
          positionA,
          positionB,
        };
        return;
      }

      // A few special behaviors when placing areas of concern:
      if (
        floorplanCoords &&
        placementModeRef.current.type === 'areaofconcern'
      ) {
        let position = floorplanCoords;
        let positionViewport = FloorplanCoordinates.toViewportCoordinates(
          position,
          context.floorplan,
          viewport
        );

        placementModeRef.current = {
          type: 'areaofconcern' as const,
          vertices: placementModeRef.current.vertices,
          mousePosition: floorplanCoords,
          nextPointPosition: position,
          mouseOverFinalPoint: false,
          nextPointSelfIntersection:
            placementModeRef.current.nextPointSelfIntersection,
        };

        // Apply any angle snapping, if enabled
        const angleSnappingEnabled = evt.shiftKey;
        if (
          angleSnappingEnabled &&
          placementModeRef.current.vertices.length > 0
        ) {
          const finalVertexViewport =
            FloorplanCoordinates.toViewportCoordinates(
              placementModeRef.current.vertices[
                placementModeRef.current.vertices.length - 1
              ],
              context.floorplan,
              viewport
            );
          const snappedCoordinates = snapToAngle(
            finalVertexViewport,
            positionViewport
          );

          // Update the position to be at the snap result point
          positionViewport = ViewportCoordinates.create(
            snappedCoordinates.x,
            snappedCoordinates.y
          );
          position = ViewportCoordinates.toFloorplanCoordinates(
            positionViewport,
            viewport,
            context.floorplan
          );
        }

        // Snap to walls if they exist
        if (!FloorplanCollection.isEmpty(walls)) {
          // Get all wall segments that should be checked for snapping
          const verticesPlusPosition = [
            ...placementModeRef.current.vertices,
            floorplanCoords, // The actual mouse position
            position, // Potentially the snap point position which could be different than the mouse position
          ];
          let [upperLeft, lowerRight] =
            computeBoundingRegionExtents(verticesPlusPosition);
          if (upperLeft && lowerRight) {
            // Pad the bounding box a little on each side so that wall segments within the snap
            // threshold outside of the regular bounding region will be included
            const paddingInPixels =
              WALL_SEGMENT_SNAP_THRESHOLD_PIXELS *
              context.floorplan.scale *
              viewport.zoom;
            upperLeft = FloorplanCoordinates.create(
              upperLeft.x - paddingInPixels,
              upperLeft.y - paddingInPixels
            );
            lowerRight = FloorplanCoordinates.create(
              lowerRight.x + paddingInPixels,
              lowerRight.y + paddingInPixels
            );

            const wallSegmentsInRegion =
              WallSegment.computeWallSegmentsInBoundingRegion(
                FloorplanCollection.list(walls),
                upperLeft,
                lowerRight
              );

            const [snapPoint] =
              WallSegment.computeNearestSnapPointInBoundingRegion(
                wallSegmentsInRegion,
                position,
                upperLeft,
                lowerRight,
                context.floorplan,
                viewport,
                WALL_SEGMENT_SNAP_THRESHOLD_PIXELS
              );
            if (snapPoint) {
              position = snapPoint;
              positionViewport = FloorplanCoordinates.toViewportCoordinates(
                snapPoint,
                context.floorplan,
                viewport
              );
            }
          }
        }

        // If the mouse if over the final point, then set a flag
        if (placementModeRef.current.vertices.length > 2) {
          const firstVertex = placementModeRef.current.vertices[0];
          const firstVertexViewport =
            FloorplanCoordinates.toViewportCoordinates(
              firstVertex,
              context.floorplan,
              viewport
            );

          const distanceToFirstVertex = Math.hypot(
            Math.abs(positionViewport.y - firstVertexViewport.y),
            Math.abs(positionViewport.x - firstVertexViewport.x)
          );

          if (distanceToFirstVertex < 8) {
            placementModeRef.current.mouseOverFinalPoint = true;
          }
        }

        // If the mouse intersects a previous line in the polygon, then set a flag
        if (
          placementModeRef.current.vertices.length > 2 &&
          placementModeRef.current.nextPointPosition
        ) {
          const lastVertex =
            placementModeRef.current.vertices[
              placementModeRef.current.vertices.length - 1
            ];

          const testLinePointA = FloorplanCoordinates.toViewportCoordinates(
            lastVertex,
            context.floorplan,
            viewport
          );
          const testLinePointB = FloorplanCoordinates.toViewportCoordinates(
            placementModeRef.current.nextPointPosition,
            context.floorplan,
            viewport
          );

          // Get all edges that make up the polygon being drawn
          const edges = computePolygonEdges(placementModeRef.current.vertices)
            .slice(0, -1)
            .map(([pointA, pointB]) => [
              FloorplanCoordinates.toViewportCoordinates(
                pointA,
                context.floorplan,
                viewport
              ),
              FloorplanCoordinates.toViewportCoordinates(
                pointB,
                context.floorplan,
                viewport
              ),
            ]);

          // Check to see if the new line being drawn intersects with any existing polygon edge
          const intersectingPoints = edges.flatMap((edge, index) => {
            const point = lineSegmentIntersection2d(
              [],
              // The edge is one line segment
              [edge[0].x, edge[0].y],
              [edge[1].x, edge[1].y],
              // And the new edge currently being created is the other line segment
              [testLinePointA.x, testLinePointA.y],
              [testLinePointB.x, testLinePointB.y]
            );

            if (!point) {
              return [];
            }

            // An intersection may be returned for the point where the two edges join, but this is a
            // false positive
            if (
              (point[0] === testLinePointA.x &&
                point[1] === testLinePointA.y) ||
              (point[0] === testLinePointB.x && point[1] === testLinePointB.y)
            ) {
              return [];
            }
            if (
              (edges[edges.length - 1][1].x === point[0] &&
                edges[edges.length - 1][1].y === point[1]) ||
              (edges[edges.length - 1][1].x === point[0] &&
                edges[edges.length - 1][1].y === point[1])
            ) {
              return [];
            }

            return [ViewportCoordinates.create(point[0], point[1])];
          });

          placementModeRef.current.nextPointSelfIntersection =
            intersectingPoints.length > 0
              ? ViewportCoordinates.toFloorplanCoordinates(
                  intersectingPoints[0],
                  viewport,
                  context.floorplan
                )
              : null;
        }

        placementModeRef.current.nextPointPosition = position;
        return;
      }
    },
    [context.floorplan, context.viewport, spaces, walls]
  );

  const onPlacementModeActivation = useCallback(() => {
    const isPlacementModeEnabled = placementModeRef.current !== null;
    if (!isPlacementModeEnabled) {
      return;
    }

    if (!context.viewport.current) {
      return;
    }

    const canvasElement = context.app.view;
    const canvasBBox = canvasElement.getBoundingClientRect();

    // Register mouse handlers
    const onMouseDownCanvas = (evt: MouseEvent) => {
      if (!context.viewport.current) {
        return;
      }

      const viewportCoords = ViewportCoordinates.create(
        evt.clientX - canvasBBox.x,
        evt.clientY - canvasBBox.y
      );
      const floorplanCoords = ViewportCoordinates.toFloorplanCoordinates(
        viewportCoords,
        context.viewport.current,
        context.floorplan
      );
      onMouseDown(floorplanCoords, evt);
    };
    const onMouseMoveCanvas = (evt: MouseEvent) => {
      if (!context.viewport.current) {
        return;
      }

      mousePositionRef.current = ViewportCoordinates.create(
        evt.clientX - canvasBBox.x,
        evt.clientY - canvasBBox.y
      );

      const floorplanCoords = ViewportCoordinates.toFloorplanCoordinates(
        mousePositionRef.current,
        context.viewport.current,
        context.floorplan
      );
      onMouseMove(floorplanCoords, evt);
    };
    const onMouseOutCanvas = () => {
      mousePositionRef.current = null;
    };
    const onKeyDownWindow = (evt: KeyboardEvent) => {
      if (
        evt.code !== 'Space' ||
        evt.repeat ||
        isTypingTarget(evt.target) ||
        !placementModeRef.current ||
        placementModeRef.current.type !== 'space' ||
        placementModeRef.current.shape !== 'desk-bench'
      ) {
        return;
      }

      placementModeRef.current = {
        ...placementModeRef.current,
        sideMode: cycleDeskBenchSideMode(placementModeRef.current.sideMode),
      };
      evt.preventDefault();
    };

    canvasElement.addEventListener('mousedown', onMouseDownCanvas);
    canvasElement.addEventListener('mousemove', onMouseMoveCanvas);
    canvasElement.addEventListener('mouseout', onMouseOutCanvas);
    window.addEventListener('keydown', onKeyDownWindow);
    onMouseDownCanvasRef.current = onMouseDownCanvas;
    onMouseMoveCanvasRef.current = onMouseMoveCanvas;
    onMouseOutCanvasRef.current = onMouseOutCanvas;
    onKeyDownWindowRef.current = onKeyDownWindow;

    const objectPlacementLabel = new MetricLabel('', {
      backgroundColor: toRawHex(Gray700),
      pinHorizontal: 'start',
      textStyle: new PIXI.TextStyle({
        // FIXME: load correct font here
        fontFamily: 'Arial',
        fontSize: 14,
        fontWeight: 'bold',
        fill: '#ffffff',
      }),
      horizontalPaddingPixels: 16,
      verticalPaddingPixels: 9,
      radiusPixels: 32,
    });
    objectPlacementLabel.name = 'object-placement-label';
    context.app.stage.addChild(objectPlacementLabel);

    const deskBenchPreviewShape = new PIXI.Graphics();
    deskBenchPreviewShape.name = DESK_BENCH_PREVIEW_SHAPE_NAME;
    deskBenchPreviewShape.visible = false;
    context.app.stage.addChild(deskBenchPreviewShape);

    // A transparent backgrop is rendered overtop of the whole stage
    // to ensure that we have control over the mouse position
    const backdrop = new PIXI.Sprite(PIXI.Texture.WHITE);
    backdrop.name = 'object-placement-backdrop';
    backdrop.width = context.viewport.current.width;
    backdrop.height = context.viewport.current.height;
    backdrop.alpha = 0;
    backdrop.interactive = true;
    backdrop.cursor = 'copy';
    context.app.stage.addChild(backdrop);
  }, [
    onMouseDown,
    onMouseMove,
    context.app.stage,
    context.app.view,
    context.floorplan,
    context.viewport,
  ]);

  const onPlacementModeDeactivation = useCallback(() => {
    const backdrop = context.app.stage.getChildByName(
      'object-placement-backdrop'
    ) as PIXI.Sprite | null;
    if (backdrop) {
      context.app.stage.removeChild(backdrop);
    }
    const objectPlacementLabel = context.app.stage.getChildByName(
      'object-placement-label'
    ) as MetricLabel | null;
    if (objectPlacementLabel) {
      context.app.stage.removeChild(objectPlacementLabel);
    }
    const deskBenchPreviewShape = context.app.stage.getChildByName(
      DESK_BENCH_PREVIEW_SHAPE_NAME
    ) as PIXI.Graphics | null;
    if (deskBenchPreviewShape) {
      context.app.stage.removeChild(deskBenchPreviewShape);
    }

    mousePositionRef.current = null;

    const canvasElement = context.app.view;
    if (onMouseDownCanvasRef.current) {
      canvasElement.removeEventListener(
        'mousedown',
        onMouseDownCanvasRef.current
      );
    }
    if (onMouseMoveCanvasRef.current) {
      canvasElement.removeEventListener(
        'mousemove',
        onMouseMoveCanvasRef.current
      );
    }
    if (onMouseOutCanvasRef.current) {
      canvasElement.removeEventListener(
        'mouseout',
        onMouseOutCanvasRef.current
      );
    }
    if (onKeyDownWindowRef.current) {
      window.removeEventListener('keydown', onKeyDownWindowRef.current);
    }
  }, [context.app.stage, context.app.view]);

  useEffect(() => {
    const oldPlacementMode = placementModeRef.current;
    if (oldPlacementMode === null && placementMode) {
      placementModeRef.current = placementMode;
      onPlacementModeActivation();
    } else if (oldPlacementMode && placementMode === null) {
      placementModeRef.current = placementMode;
      onPlacementModeDeactivation();
    } else if (oldPlacementMode && placementMode) {
      // Filter out situations where the old and new placement modes are in fact the same thing
      switch (oldPlacementMode.type) {
        case 'threshold':
          if (
            placementMode.type === 'threshold' &&
            oldPlacementMode.positionA
          ) {
            return;
          }
          break;
        case 'space':
          if (
            placementMode.type === 'space' &&
            oldPlacementMode.shape === placementMode.shape &&
            oldPlacementMode.countingMode === placementMode.countingMode
          ) {
            if (
              oldPlacementMode.shape !== 'desk-bench' ||
              (placementMode.shape === 'desk-bench' &&
                oldPlacementMode.deskWidthMeters ===
                  placementMode.deskWidthMeters &&
                oldPlacementMode.deskDepthMeters ===
                  placementMode.deskDepthMeters)
            ) {
              return;
            }
          }
          break;
        case 'sensor':
          if (
            placementMode.type === 'sensor' &&
            oldPlacementMode.sensorType === placementMode.sensorType
          ) {
            return;
          }
          break;
        case 'areaofconcern':
          if (placementMode.type === 'areaofconcern') {
            return;
          }
          break;
        case 'reference':
          if (
            placementMode.type === 'reference' &&
            oldPlacementMode.referenceType === placementMode.referenceType
          ) {
            return;
          }
          break;
        case 'photogroup':
          if (placementMode.type === 'photogroup') {
            return;
          }
          break;
      }

      // If they differ, then update the state.
      placementModeRef.current = null;
      onPlacementModeDeactivation();
      placementModeRef.current = placementMode;
      onPlacementModeActivation();
    }
  }, [placementMode, onPlacementModeActivation, onPlacementModeDeactivation]);
  useEffect(() => {
    return () => onPlacementModeDeactivation();
  }, [onPlacementModeDeactivation]);

  return (
    <Fragment>
      <Layer
        onAnimationFrame={() => {
          const objectPlacementLabel = context.app.stage.getChildByName(
            'object-placement-label'
          ) as MetricLabel | undefined;

          const addPlacementText = PlacementMode.computeText(
            placementModeRef.current
          );

          if (!objectPlacementLabel) {
            return;
          } else if (!addPlacementText) {
            objectPlacementLabel.visible = false;
            return;
          }

          // If the mouse is no longer on the canvas, then hide the label
          if (!mousePositionRef.current) {
            objectPlacementLabel.visible = false;
            return;
          }

          objectPlacementLabel.visible = true;
          objectPlacementLabel.x =
            mousePositionRef.current.x + PLACEMENT_TOOLTIP_OFFSET_X_PX;
          objectPlacementLabel.y = mousePositionRef.current.y;

          if (addPlacementText) {
            objectPlacementLabel.setText(addPlacementText);
          }
        }}
      />
      <Layer
        onAnimationFrame={() => {
          if (!context.viewport.current) {
            return;
          }

          const deskBenchPreviewShape = context.app.stage.getChildByName(
            DESK_BENCH_PREVIEW_SHAPE_NAME
          ) as PIXI.Graphics | undefined;
          if (!deskBenchPreviewShape) {
            return;
          }

          deskBenchPreviewShape.clear();
          deskBenchPreviewShape.visible = false;

          if (
            !placementModeRef.current ||
            placementModeRef.current.type !== 'space' ||
            placementModeRef.current.shape !== 'desk-bench' ||
            !placementModeRef.current.positionA ||
            !placementModeRef.current.positionB
          ) {
            return;
          }

          const viewport = context.viewport.current;
          const color =
            placementModeRef.current.countingMode === 'waffle'
              ? Yellow400
              : Purple400;
          const colorRaw = toRawHex(color);
          const polygons = computeDeskBenchPolygons(
            placementModeRef.current.positionA,
            placementModeRef.current.positionB,
            placementModeRef.current.deskWidthMeters,
            placementModeRef.current.deskDepthMeters,
            placementModeRef.current.sideMode
          );

          deskBenchPreviewShape.visible = true;
          polygons.forEach((vertices) => {
            const viewportVertices = vertices.map((vertex) =>
              FloorplanCoordinates.toViewportCoordinates(
                vertex,
                context.floorplan,
                viewport
              )
            );
            deskBenchPreviewShape.beginFill(colorRaw, 0.12);
            deskBenchPreviewShape.lineStyle({ width: 1, color: colorRaw });
            drawPolygon(deskBenchPreviewShape, viewportVertices);
            deskBenchPreviewShape.endFill();
          });

          const startViewport = FloorplanCoordinates.toViewportCoordinates(
            placementModeRef.current.positionA,
            context.floorplan,
            viewport
          );
          const endViewport = FloorplanCoordinates.toViewportCoordinates(
            placementModeRef.current.positionB,
            context.floorplan,
            viewport
          );
          deskBenchPreviewShape.lineStyle({ width: 2, color: colorRaw });
          deskBenchPreviewShape.moveTo(startViewport.x, startViewport.y);
          deskBenchPreviewShape.lineTo(endViewport.x, endViewport.y);
        }}
      />
      <PolygonalSpaceCreationLayer placementModeRef={placementModeRef} />
      <AreaOfConcernCreationLayer placementModeRef={placementModeRef} />
      <ReferenceRulerCreationLayer placementModeRef={placementModeRef} />
      <ThresholdCreationLayer placementModeRef={placementModeRef} />
    </Fragment>
  );
};

export default ObjectPlacementTargetLayer;
